import { Button } from '@/components/Button/Button'
import { Chip } from '@/components/Chip/Chip'
import { Popup } from '@/components/Popup/Popup'
import { TextArea } from '@/components/TextArea/TextArea'
import { useCopy } from '@/hooks/useCopy'
import { GeneratorFormContext } from '@/hooks/useGeneratorForm'
import CheckSvg from '@/icons/check.svg?react'
import CopySvg from '@/icons/copy.svg?react'
import { usePostsState } from '@/state/posts'
import { useProjectsState } from '@/state/projects'
import debounce from 'lodash.debounce'
import type { FunctionalComponent } from 'preact'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

export const PostForm: FunctionalComponent = () => {
  const copy = useCopy()
  const form = useContext(GeneratorFormContext)!
  const patchPost = usePostsState(s => s.patch)
  const setPostContent = usePostsState(s => s.setContent)

  const draft = useRef<{ id: number; content: string }>()

  const [index, setIndex] = useState(0)

  const project = useProjectsState(useShallow(
    s => s.list.find(item => item.id === form.projectId),
  ))

  const xLink = useMemo(() => {
    const text = form.variants.at(index)
    if (!text) return null
    const encoded = encodeURIComponent(text)
    return `https://twitter.com/intent/tweet?text=${encoded}`
  }, [form.variants])

  const saveDeboucned = useCallback(
    debounce((content: string) => {
      if (form.postId !== undefined) {
        patchPost(form.postId, { content })
      }
    }, 800),
    [form.postId],
  )

  useEffect(() => {
    return () => {
      if (draft.current) {
        setPostContent(draft.current.id, draft.current.content)
      }
    }
  }, [])

  return (
    <>
      <Popup.Title>
        Post for {project?.title ?? 'Unknown'}
      </Popup.Title>

      <Popup.Fields>
        <Chip.List>
          {form.variants.map((_, idx) => (
            <Chip
              active={index === idx}
              onClick={() => {
                try {
                  if (!form.postId) {
                    throw new Error('postId must be defined')
                  }

                  setIndex(idx)

                  const content = form.variants[idx]

                  draft.current = {
                    content,
                    id: form.postId,
                  }

                  patchPost(form.postId, { content })
                }
                catch (e) {
                  console.error(e)
                }
              }}
            >
              {idx == 0 ? 'Variant 1' : idx + 1}
            </Chip>
          ))}
        </Chip.List>

        <TextArea
          value={form.variants.at(index)}
          onChange={value => {
            saveDeboucned(value)

            if (form.postId) {
              draft.current = {
                content: value,
                id: form.postId,
              }
            }

            form.setVariants(prev => {
              const next = [...prev]
              next[index] = value
              return next
            })
          }}
        />
      </Popup.Fields>

      <Popup.Footer>
        <Popup.Buttons />
        <Popup.Buttons>
          <CopyToClipboard
            text={form.variants.at(index) || ''}
            onCopy={copy.setSuccess}
          >
            <Button class={styles.copy}>
              Copy
              {copy.copied ? <CheckSvg /> : <CopySvg />}
            </Button>
          </CopyToClipboard>

          {xLink && (
            <Button
              type='primary'
              onClick={() => {
                window.open(xLink, '_blank')
              }}
            >
              Post to X
            </Button>
          )}
        </Popup.Buttons>
      </Popup.Footer>
    </>
  )
}
